import React from "react";

/**
 * BrandMarkOutline — sketched outline of the Holy Words icon used
 * specifically for the QuickShortcuts opener (the small mark that
 * pops the chip rail). The user explicitly wants this mark to be a
 * SKETCH — never the full-color app icon — so it reads as a "hint /
 * shortcut handle" rather than a duplicate of the main brand badge.
 *
 * Pure stroke (no fills) so it inherits the parent's text color via
 * `currentColor` — works equally well on cream and forest backgrounds.
 *
 * Props:
 *   size      – pixel dimension (square). Defaults to 24.
 *   color     – stroke color. Defaults to "currentColor".
 *   className – additional Tailwind classes for the outer wrapper.
 */
export default function BrandMarkOutline({ size = 24, color = "currentColor", className = "" }) {
  return (
    <span
      className={`inline-block shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-label="Holy Words"
      data-testid="brand-mark-outline"
    >
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
      >
        {/* Outer rounded-square frame — echoes the full app icon's
            shape without filling the area. */}
        <rect
          x="4"
          y="4"
          width="56"
          height="56"
          rx="13"
          ry="13"
          fill="none"
          stroke={color}
          strokeWidth="2.5"
        />

        {/* HW wordmark — stroked, not filled, so it reads as a
            sketch of the real logo. Slight letterspacing tightens
            the H-W kerning so the two letters feel like one mark. */}
        <text
          x="32"
          y="42"
          textAnchor="middle"
          fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"
          fontWeight="900"
          fontSize="26"
          letterSpacing="-1"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
        >
          HW
        </text>

        {/* Tiny play-triangle accent — matches the gold play arrow in
            the bottom-right of the full app icon, but rendered as a
            small open triangle so it reads as a sketch annotation. */}
        <path
          d="M46 50 L54 53.5 L46 57 Z"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
